import { useState } from "react";
import EmojiPicker from "@/components/EmojiPicker";
import { useServices, ServiceCategoryDB, SubServiceDB } from "@/hooks/useServices";
import { useServiceAddons, ServiceAddon } from "@/hooks/useServiceAddons";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Check, X, Plus, Trash2 } from "lucide-react";
import { toTitleCase } from "@/lib/titleCase";

const AddonsSection = ({ categoryId }: { categoryId: string }) => {
  const { addons, refetch } = useServiceAddons(categoryId);
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({ name: "", price: 0, duration: "" });
  const [addDialog, setAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", duration: "15 minutes", price: 0 });
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const saveNew = async () => {
    if (!addForm.name.trim()) return;
    const maxOrder = Math.max(0, ...addons.map(a => a.sort_order));
    await supabase.from("service_addons" as any).insert({
      category_id: categoryId,
      name: toTitleCase(addForm.name),
      duration: addForm.duration,
      price: addForm.price,
      sort_order: maxOrder + 1,
    } as any);
    setAddDialog(false);
    setAddForm({ name: "", duration: "15 minutes", price: 0 });
    toast({ title: "Added", description: `${addForm.name} add-on created` });
    refetch();
  };

  const startEdit = (a: ServiceAddon) => {
    setEditingId(a.id);
    setEditValues({ name: a.name, price: a.price, duration: a.duration });
  };

  const saveEdit = async (id: string) => {
    await supabase.from("service_addons" as any).update({
      name: toTitleCase(editValues.name),
      price: editValues.price,
      duration: editValues.duration,
    } as any).eq("id", id);
    setEditingId(null);
    toast({ title: "Updated", description: `${editValues.name} updated` });
    refetch();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await supabase.from("service_addons" as any).delete().eq("id", deleteTarget.id);
    toast({ title: "Deleted", description: `${deleteTarget.name} deleted` });
    setDeleteTarget(null);
    refetch();
  };

  return (
    <div className="mt-4 pt-4 border-t border-border/30">
      <h4 className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium mb-3 flex items-center gap-2">
        <Plus size={12} /> Add-Ons ({addons.length})
      </h4>
      {addons.length === 0 && (
        <p className="text-xs text-muted-foreground italic mb-2">No add-ons yet</p>
      )}
      <div className="space-y-2">
        {addons.map((addon) => {
          const isEditing = editingId === addon.id;
          return (
            <div key={addon.id} className="flex items-center justify-between border-b border-border/20 pb-2 last:border-0 gap-3">
              {isEditing ? (
                <>
                  <Input className="h-7 text-xs bg-card flex-1" value={editValues.name} onChange={(e) => setEditValues({ ...editValues, name: e.target.value })} />
                  <Input className="w-24 h-7 text-xs bg-card" value={editValues.duration} onChange={(e) => setEditValues({ ...editValues, duration: e.target.value })} />
                  <Input type="number" className="w-16 h-7 text-xs bg-card" value={editValues.price} onChange={(e) => setEditValues({ ...editValues, price: Number(e.target.value) })} />
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveEdit(addon.id)}><Check size={12} className="text-primary" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}><X size={12} /></Button>
                </>
              ) : (
                <>
                  <span className="text-xs font-medium flex-1">{addon.name}</span>
                  <span className="text-[10px] text-muted-foreground">{addon.duration}</span>
                  <span className="text-xs font-medium">+${addon.price}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(addon)}><Pencil size={12} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteTarget({ id: addon.id, name: addon.name })}><Trash2 size={12} /></Button>
                </>
              )}
            </div>
          );
        })}
      </div>
      <Button variant="ghost" size="sm" className="mt-2 gap-2 text-xs text-muted-foreground" onClick={() => setAddDialog(true)}>
        <Plus size={12} /> Add Add-On
      </Button>

      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Add-On</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm text-muted-foreground">Name</label><Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm text-muted-foreground">Duration</label><Input value={addForm.duration} onChange={(e) => setAddForm({ ...addForm, duration: e.target.value })} /></div>
              <div><label className="text-sm text-muted-foreground">Price ($)</label><Input type="number" value={addForm.price} onChange={(e) => setAddForm({ ...addForm, price: Number(e.target.value) })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button>
            <Button onClick={saveNew}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This add-on will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Services = () => {
  const { categories, loading, refetch } = useServices();
  const { toast } = useToast();

  // Inline editing for sub-services
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ name: string; description: string; price: number; duration: string }>({
    name: "", description: "", price: 0, duration: "",
  });

  // Category dialog
  const [catDialog, setCatDialog] = useState(false);
  const [editingCat, setEditingCat] = useState<ServiceCategoryDB | null>(null);
  const [catForm, setCatForm] = useState({ title: "", tagline: "", icon: "✨" });

  // Sub-service dialog
  const [subDialog, setSubDialog] = useState(false);
  const [subCategoryId, setSubCategoryId] = useState<string>("");
  const [subForm, setSubForm] = useState({ name: "", description: "", duration: "30 min", price: 0 });

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{ type: "category" | "service"; id: string; name: string } | null>(null);

  // --- Category CRUD ---
  const openNewCategory = () => {
    setEditingCat(null);
    setCatForm({ title: "", tagline: "", icon: "✨" });
    setCatDialog(true);
  };

  const openEditCategory = (cat: ServiceCategoryDB) => {
    setEditingCat(cat);
    setCatForm({ title: cat.title, tagline: cat.tagline, icon: cat.icon });
    setCatDialog(true);
  };

  const saveCategory = async () => {
    if (!catForm.title.trim()) return;
    if (editingCat) {
      await supabase.from("service_categories").update({
        title: toTitleCase(catForm.title), tagline: catForm.tagline, icon: catForm.icon,
      }).eq("id", editingCat.id);
      toast({ title: "Updated", description: `${catForm.title} category updated` });
    } else {
      const maxOrder = Math.max(0, ...categories.map((c) => c.sort_order));
      await supabase.from("service_categories").insert({
        title: toTitleCase(catForm.title), tagline: catForm.tagline, icon: catForm.icon, sort_order: maxOrder + 1,
      });
      toast({ title: "Created", description: `${catForm.title} category added` });
    }
    setCatDialog(false);
    refetch();
  };

  // --- Sub-service CRUD ---
  const openNewSubService = (categoryId: string) => {
    setSubCategoryId(categoryId);
    setSubForm({ name: "", description: "", duration: "30 min", price: 0 });
    setSubDialog(true);
  };

  const saveNewSubService = async () => {
    if (!subForm.name.trim()) return;
    const cat = categories.find((c) => c.id === subCategoryId);
    const maxOrder = cat ? Math.max(0, ...cat.subServices.map((s) => s.sort_order)) : 0;
    await supabase.from("sub_services").insert({
      category_id: subCategoryId,
      name: toTitleCase(subForm.name),
      description: subForm.description,
      duration: subForm.duration,
      price: subForm.price,
      sort_order: maxOrder + 1,
    });
    setSubDialog(false);
    toast({ title: "Added", description: `${subForm.name} added` });
    refetch();
  };

  const startEditSub = (sub: SubServiceDB) => {
    setEditingId(sub.id);
    setEditValues({ name: sub.name, description: sub.description, price: sub.price, duration: sub.duration });
  };

  const saveEditSub = async (id: string) => {
    await supabase.from("sub_services").update({
      name: toTitleCase(editValues.name),
      description: editValues.description,
      price: editValues.price,
      duration: editValues.duration,
    }).eq("id", id);
    setEditingId(null);
    toast({ title: "Updated", description: `${editValues.name} updated` });
    refetch();
  };

  // --- Delete ---
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === "category") {
      await supabase.from("service_categories").delete().eq("id", deleteTarget.id);
    } else {
      await supabase.from("sub_services").delete().eq("id", deleteTarget.id);
    }
    toast({ title: "Deleted", description: `${deleteTarget.name} deleted` });
    setDeleteTarget(null);
    refetch();
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading services…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold italic">Services</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage service categories, offerings, and add-ons</p>
        </div>
        <Button onClick={openNewCategory} className="gap-2">
          <Plus size={16} /> Add Category
        </Button>
      </div>

      <div className="grid gap-6">
        {categories.map((cat) => (
          <Card key={cat.id} className="bg-card border-border/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-3">
                <span className="text-2xl">{cat.icon}</span>
                <div>
                  <span className="font-serif text-xl italic">{cat.title}</span>
                  <p className="text-xs text-muted-foreground font-normal font-sans not-italic">{cat.tagline}</p>
                </div>
              </CardTitle>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEditCategory(cat)}>
                  <Pencil size={14} />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeleteTarget({ type: "category", id: cat.id, name: cat.title })}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {cat.subServices.map((sub) => {
                  const isEditing = editingId === sub.id;
                  return (
                    <div key={sub.id} className="flex items-start justify-between border-b border-border/30 pb-3 last:border-0 gap-4">
                      {isEditing ? (
                        <>
                          <div className="flex-1 space-y-2">
                            <Input className="h-8 text-sm bg-card" value={editValues.name} onChange={(e) => setEditValues({ ...editValues, name: e.target.value })} placeholder="Service name" />
                            <Textarea className="text-sm bg-card min-h-[40px]" value={editValues.description} onChange={(e) => setEditValues({ ...editValues, description: e.target.value })} placeholder="Description" />
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Input className="w-20 h-8 text-sm bg-card" value={editValues.duration} onChange={(e) => setEditValues({ ...editValues, duration: e.target.value })} placeholder="Duration" />
                            <Input type="number" className="w-20 h-8 text-sm bg-card" value={editValues.price} onChange={(e) => setEditValues({ ...editValues, price: Number(e.target.value) })} />
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => saveEditSub(sub.id)}><Check size={14} className="text-primary" /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingId(null)}><X size={14} /></Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{sub.name}</p>
                            <p className="text-xs text-muted-foreground">{sub.description}</p>
                          </div>
                          <div className="flex items-center gap-4 shrink-0">
                            <span className="text-xs text-muted-foreground">{sub.duration}</span>
                            <span className="text-sm font-medium">${sub.price}</span>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEditSub(sub)}><Pencil size={14} /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeleteTarget({ type: "service", id: sub.id, name: sub.name })}><Trash2 size={14} /></Button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              <Button variant="ghost" size="sm" className="mt-3 gap-2 text-xs text-muted-foreground" onClick={() => openNewSubService(cat.id)}>
                <Plus size={14} /> Add Service
              </Button>

              {/* Add-ons section per category */}
              <AddonsSection categoryId={cat.id} />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Category Dialog */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCat ? "Edit Category" : "New Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Icon (emoji)</label>
              <EmojiPicker value={catForm.icon} onChange={(emoji) => setCatForm({ ...catForm, icon: emoji })} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Title</label>
              <Input value={catForm.title} onChange={(e) => setCatForm({ ...catForm, title: e.target.value })} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Tagline</label>
              <Input value={catForm.tagline} onChange={(e) => setCatForm({ ...catForm, tagline: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog(false)}>Cancel</Button>
            <Button onClick={saveCategory}>{editingCat ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-service Dialog */}
      <Dialog open={subDialog} onOpenChange={setSubDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Service</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Name</label>
              <Input value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Description</label>
              <Textarea value={subForm.description} onChange={(e) => setSubForm({ ...subForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Duration</label>
                <Input value={subForm.duration} onChange={(e) => setSubForm({ ...subForm, duration: e.target.value })} />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Price ($)</label>
                <Input type="number" value={subForm.price} onChange={(e) => setSubForm({ ...subForm, price: Number(e.target.value) })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubDialog(false)}>Cancel</Button>
            <Button onClick={saveNewSubService}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === "category"
                ? "This will delete the category and all its services. This action cannot be undone."
                : "This service will be permanently removed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Services;
